import React, { useState, useEffect } from 'react';
import api from '../api/api'; 
import { Megaphone, CheckCircle2, AlertCircle, Trash2, Power, Eye, EyeOff } from 'lucide-react';

const BannerPage = ({ isGlobalOnly = false }) => {
  const [bannerText, setBannerText] = useState("");
  const [banners, setBanners] = useState([]); 
  const [status, setStatus] = useState({ msg: '', type: '' });
  const [isWinnerBannerVisible, setIsWinnerBannerVisible] = useState(true);
  const [marqueeSequence, setMarqueeSequence] = useState([]);

  // Data Fetch & Sequence Logic
  const fetchAllData = async () => {
    try {
      const [bannerRes, winnerRes] = await Promise.allSettled([
        api.get('/api/admin/banners'),
        api.get('/api/spin/latest-winners')
      ]);

      let activeAdminTexts = [];
      let winnerTexts = [];
      let isVisible = false;

      // Handle Banners Data
      if (bannerRes.status === 'fulfilled') {
        setBanners(bannerRes.value.data || []);
        activeAdminTexts = (bannerRes.value.data || []).filter(b => b.isActive).map(b => b.text);
      }

      // Handle Winners Data
      if (winnerRes.status === 'fulfilled') {
        const winnersData = winnerRes.value.data.winners || [];
        winnerTexts = winnersData.map(w => `🏆 Congratulations ${w.playerName} won ${w.wonValue}`);
        isVisible = winnerRes.value.data.isVisible !== undefined ? winnerRes.value.data.isVisible : true;
        setIsWinnerBannerVisible(isVisible);
      }

      let combined = [];
      if (isVisible && winnerTexts.length > 0) {
        const maxLength = Math.max(activeAdminTexts.length, winnerTexts.length);
        for (let i = 0; i < maxLength; i++) {
          if (activeAdminTexts[i]) combined.push(activeAdminTexts[i]);
          if (winnerTexts[i]) combined.push(winnerTexts[i]);
        }
      } else {
        combined = activeAdminTexts;
      }
      
      setMarqueeSequence(combined);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 30000); // 30 seconds auto-refresh
    return () => clearInterval(interval);
  }, []);

  const showStatus = (msg, type) => {
    setStatus({ msg, type });
    setTimeout(() => setStatus({ msg: '', type: '' }), 4000);
  };

  const toggleWinnerBanner = async () => {
    try {
      const res = await api.put('/api/admin/winners-banner/toggle');
      setIsWinnerBannerVisible(res.data.isVisible); // Instant UI update
      showStatus(`Top Global Banner Updated!`, "success");
      fetchAllData(); 
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to toggle (API missing or Server Error)";
      showStatus(errorMessage, "error");
    }
  };

  const addBanner = async () => {
    if (!bannerText.trim()) return showStatus("Please enter text", "error");
    try {
      await api.post('/api/admin/banners', { 
        text: bannerText.trim(), 
        isActive: true 
      });
      setBannerText("");
      showStatus("Added to Global Sequence!", "success");
      fetchAllData(); 
    } catch (err) {
      showStatus(err.response?.data?.error || "Failed to add banner", "error");
    }
  };

  const toggleBanner = async (id) => {
    try {
      await api.put(`/api/admin/banners/toggle/${id}`);
      fetchAllData();
    } catch (err) {
      showStatus(err.response?.data?.error || "Failed to update status", "error");
    }
  };

  const deleteBanner = async (id) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;
    try {
      await api.delete(`/api/admin/banners/${id}`);
      showStatus("Removed from Global Banner!", "success");
      fetchAllData();
    } catch (err) {
      showStatus(err.response?.data?.error || "Failed to delete", "error");
    }
  };

  return (
    <div className={isGlobalOnly ? "" : "bg-gray-50 min-h-screen"}>
      <style>
        {`
          @keyframes scrollGlobal {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-global-marquee {
            display: flex;
            align-items: center;
            white-space: nowrap;
            width: max-content;
            animation: scrollGlobal 390s linear infinite;
          }
          .global-marquee-text {
            color: #2D1E0B !important;
            font-weight: 700;
            letter-spacing: 0.5px;
          }
          .gift-icon {
            color: #2D1E0B;
          }
        `}
      </style>

      {/* FIXED TOP MARQUEE BANNER (UPDATED DESIGN) */}
      <div 
        className="py-3 overflow-hidden fixed top-0 left-0 right-0 z-[9999] w-full" 
        style={{ 
          background: 'linear-gradient(90deg, #F9DD76 0%, #D4A341 20%, #FDF1A9 45%, #C28D2B 75%, #F0CE68 100%)', 
          borderBottom: '2px solid #7E4917',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)' 
        }}
      >
        <div className="animate-global-marquee font-bold text-lg md:text-xl uppercase">
          {marqueeSequence.length > 0 ? (
            <>
              {[...marqueeSequence, ...marqueeSequence, ...marqueeSequence].map((text, index) => {
                // Backend se aanay walay text se emoji remove karna taake screenshot jesa clean text nazar aaye
                const cleanText = text.replace('🏆', '').trim();
                
                return (
                  <div key={index} className="flex items-center mx-10">
                    {/* Solid Gift Icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-3 gift-icon">
                      <path d="M9.375 3a1.875 1.875 0 000 3.75h1.875v4.5H3.375A1.875 1.875 0 011.5 9.375v-.75c0-1.036.84-1.875 1.875-1.875h3.193A3.375 3.375 0 0112 2.753a3.375 3.375 0 015.382 3.997h3.193c1.035 0 1.875.84 1.875 1.875v.75a1.875 1.875 0 01-1.875 1.875H12.75v-4.5h1.875a1.875 1.875 0 10-1.875-1.875V6.75h-1.5V4.875C11.25 3.839 10.41 3 9.375 3zM12.75 12.75h7.5v6.375a1.875 1.875 0 01-1.875 1.875h-5.625v-8.25zM11.25 12.75H3.75v6.375c0 1.035.84 1.875 1.875 1.875h5.625v-8.25z" />
                    </svg>
                    <span className="global-marquee-text">{cleanText}</span>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="flex items-center mx-10">
               <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-3 gift-icon">
                  <path d="M9.375 3a1.875 1.875 0 000 3.75h1.875v4.5H3.375A1.875 1.875 0 011.5 9.375v-.75c0-1.036.84-1.875 1.875-1.875h3.193A3.375 3.375 0 0112 2.753a3.375 3.375 0 015.382 3.997h3.193c1.035 0 1.875.84 1.875 1.875v.75a1.875 1.875 0 01-1.875 1.875H12.75v-4.5h1.875a1.875 1.875 0 10-1.875-1.875V6.75h-1.5V4.875C11.25 3.839 10.41 3 9.375 3zM12.75 12.75h7.5v6.375a1.875 1.875 0 01-1.875 1.875h-5.625v-8.25zM11.25 12.75H3.75v6.375c0 1.035.84 1.875 1.875 1.875h5.625v-8.25z" />
               </svg>
               <span className="global-marquee-text">WELCOME TO SPIN & WIN | SYSTEM ONLINE</span>
            </div>
          )}
        </div>
      </div>

      {!isGlobalOnly && (
        <div className="p-8 pt-20">
          <h1 className="text-3xl font-bold mb-8 flex items-center gap-2 text-gray-800 uppercase">
            <Megaphone className="text-yellow-600" /> Banner Control Center
          </h1>

          {/* STATUS ALERTS */}
          {status.msg && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${status.type === 'success' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
              {status.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
              <span className="font-semibold">{status.msg}</span>
            </div>
          )}

          {/* TOGGLE WINNERS VISIBILITY PANEL */}
          <div className="mb-8 p-6 bg-slate-900 text-white rounded-2xl shadow-xl flex justify-between items-center border-l-8 border-yellow-500">
            <div>
                <h2 className="text-xl font-bold flex items-center gap-2 uppercase">🏆 Winners Visibility</h2>
                <p className="text-slate-400 text-sm mt-1">Control if live spin winners are shown on the global banner.</p>
            </div>
            <button 
                onClick={toggleWinnerBanner}
                className={`flex items-center gap-2 px-8 py-3 rounded-xl font-black transition-all shadow-lg ${isWinnerBannerVisible ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}
            >
                {isWinnerBannerVisible ? <Eye size={20}/> : <EyeOff size={20}/>}
                {isWinnerBannerVisible ? 'BANNER IS ON' : 'BANNER IS OFF'}
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* ADD TEXT FORM */}
            <div className="lg:col-span-5 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-700 uppercase">Add Admin Text</h2>
              <textarea 
                className="w-full border p-4 rounded-xl h-32 mb-4 focus:ring-2 focus:ring-yellow-400 outline-none bg-gray-50 text-gray-800 resize-none"
                placeholder="Enter promotional message here..."
                value={bannerText}
                onChange={(e) => setBannerText(e.target.value)}
              ></textarea>
              <button onClick={addBanner} className="w-full bg-yellow-500 text-black py-4 rounded-xl font-black hover:bg-yellow-600 transition shadow-md uppercase">Add to List</button>
            </div>

            {/* MANAGE MESSAGES LIST */}
            <div className="lg:col-span-7 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <h2 className="text-xl font-semibold mb-4 text-gray-700 uppercase">Manage Messages</h2>
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {banners.map((b) => (
                  <div key={b.id} className={`p-4 rounded-xl border flex justify-between items-center transition-all ${b.isActive ? 'border-yellow-400 bg-yellow-50/50' : 'opacity-60 bg-gray-50'}`}>
                    <p className="text-gray-700 font-medium">{b.text}</p>
                    <div className="flex gap-2">
                      <button onClick={() => toggleBanner(b.id)} className={`p-2 rounded-lg transition-colors ${b.isActive ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600 hover:bg-green-400 hover:text-white'}`}><Power size={18} /></button>
                      <button onClick={() => deleteBanner(b.id)} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-500 hover:text-white transition-all"><Trash2 size={18} /></button>
                    </div>
                  </div>
                ))}
                {banners.length === 0 && (
                  <p className="text-gray-400 text-center py-8">No banners found. Add one from the left panel.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerPage;
































// import React, { useState, useEffect } from 'react';
// import api from '../api/api'; 
// import { Megaphone, CheckCircle2, AlertCircle, Trash2, Power, Eye, EyeOff } from 'lucide-react';

// const BannerPage = ({ isGlobalOnly = false }) => {
//   const [bannerText, setBannerText] = useState("");
//   const [banners, setBanners] = useState([]); 
//   const [status, setStatus] = useState({ msg: '', type: '' });
//   const [isWinnerBannerVisible, setIsWinnerBannerVisible] = useState(true);
//   const [marqueeSequence, setMarqueeSequence] = useState([]);

//   // Data Fetch & Sequence Logic
//   const fetchAllData = async () => {
//     try {
//       const [bannerRes, winnerRes] = await Promise.all([
//         api.get('/api/admin/banners'),
//         api.get('/api/spin/latest-winners')
//       ]);

//       const activeAdminTexts = bannerRes.data.filter(b => b.isActive).map(b => b.text);
//       const winnersData = winnerRes.data.winners || [];
//       const winnerTexts = winnersData.map(w => `🏆 Congratulations ${w.playerName} win ${w.wonValue} off`);
//       const isVisible = winnerRes.data.isVisible;

//       setBanners(bannerRes.data);
//       setIsWinnerBannerVisible(isVisible);

//       let combined = [];
//       if (isVisible && winnerTexts.length > 0) {
//         const maxLength = Math.max(activeAdminTexts.length, winnerTexts.length);
//         for (let i = 0; i < maxLength; i++) {
//           if (activeAdminTexts[i]) combined.push(activeAdminTexts[i]);
//           if (winnerTexts[i]) combined.push(winnerTexts[i]);
//         }
//       } else {
//         combined = activeAdminTexts;
//       }
      
//       setMarqueeSequence(combined);
//     } catch (err) {
//       console.error("Error fetching data:", err);
//     }
//   };

//   useEffect(() => {
//     fetchAllData();
//     const interval = setInterval(fetchAllData, 30000);
//     return () => clearInterval(interval);
//   }, []);

//   const showStatus = (msg, type) => {
//     setStatus({ msg, type });
//     setTimeout(() => setStatus({ msg: '', type: '' }), 4000);
//   };

//   const toggleWinnerBanner = async () => {
//     try {
//       // 3. api.put ka istemaal
//       await api.put('/api/admin/winners-banner/toggle');
//       showStatus(`Top Global Banner Updated!`, "success");
//       fetchAllData(); 
//     } catch (err) {
//       showStatus("Failed to toggle", "error");
//     }
//   };

//   const addBanner = async () => {
//     if (!bannerText.trim()) return showStatus("Please enter text", "error");
//     try {
//       // 4. api.post 
//       await api.post('/api/admin/banners', { 
//         text: bannerText.trim(), 
//         isActive: true 
//       });
//       setBannerText("");
//       showStatus("Added to Global Sequence!", "success");
//       fetchAllData(); 
//     } catch (err) {
//       showStatus("Failed to add", "error");
//     }
//   };

//   const toggleBanner = async (id) => {
//     try {
//       await api.put(`/api/admin/banners/toggle/${id}`);
//       fetchAllData();
//     } catch (err) {
//       showStatus("Failed to update status", "error");
//     }
//   };

//   const deleteBanner = async (id) => {
//     if (!window.confirm("Are you sure?")) return;
//     try {
//       // 6. api.delete 
//       await api.delete(`/api/admin/banners/${id}`);
//       showStatus("Removed from Global Banner!", "success");
//       fetchAllData();
//     } catch (err) {
//       showStatus("Failed to delete", "error");
//     }
//   };

//   return (
//     <div className={isGlobalOnly ? "" : "bg-gray-50 min-h-screen"}>
//       <style>
//         {`
//           @keyframes scrollGlobal {
//             0% { transform: translateX(0); }
//             100% { transform: translateX(-50%); }
//           }
//           .animate-global-marquee {
//             display: flex;
//             white-space: nowrap;
//             width: max-content;
//             animation: scrollGlobal 160s linear infinite;
//           }
//           .banner-star {
//             color: #facc15 !important;
//             text-shadow: 0 0 8px rgba(250, 204, 21, 0.9);
//           }
//           .global-marquee-text {
//             color: #E53935 !important;
//             font-weight: bold;
//           }
//         `}
//       </style>

//       <div className="text-white py-3 overflow-hidden border-b border-[#f6b040] fixed top-0 left-0 right-0 z-[9999] w-full" style={{ background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 40%, #ffffff 60%, #ffffff 100%)', boxShadow: '0 2px 20px rgba(246,176,64,0.15)' }}>
//         <div className="animate-global-marquee font-bold text-base md:text-xl uppercase">
//           {marqueeSequence.length > 0 ? (
//             <>
//               {[...marqueeSequence, ...marqueeSequence].map((text, index) => (
//                 <div key={index} className="flex items-center">
//                   <span className="mx-16 global-marquee-text">{text}</span>
//                   <span className="text-black/40 text-2xl banner-star">★</span>
//                 </div>
//               ))}
//             </>
//           ) : (
//             <div className="flex items-center">
//                <span className="mx-16 global-marquee-text">Welcome to Spin & Win | System Online</span>
//                <span className="mx-16 banner-star">★</span>
//                <span className="mx-16 global-marquee-text">Welcome to Spin & Win | System Online</span>
//             </div>
//           )}
//         </div>
//       </div>

//       {!isGlobalOnly && (
//         <div className="p-8 pt-20">
//           <h1 className="text-3xl font-bold mb-8 flex items-center gap-2 text-gray-800 uppercase">
//             <Megaphone className="text-yellow-600" /> Banner Control Center
//           </h1>

//           <div className="mb-8 p-6 bg-slate-900 text-white rounded-2xl shadow-xl flex justify-between items-center border-l-8 border-yellow-500">
//             <div>
//                 <h2 className="text-xl font-bold flex items-center gap-2 uppercase">🏆 Winners Visibility</h2>
//                 <p className="text-slate-400 text-sm mt-1"> </p>
//             </div>
//             <button 
//                 onClick={toggleWinnerBanner}
//                 className={`flex items-center gap-2 px-8 py-3 rounded-xl font-black transition-all shadow-lg ${isWinnerBannerVisible ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}`}
//             >
//                 {isWinnerBannerVisible ? <Eye size={20}/> : <EyeOff size={20}/>}
//                 {isWinnerBannerVisible ? 'BANNER IS ON' : 'BANNER IS OFF'}
//             </button>
//           </div>

//           {status.msg && (
//             <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${status.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
//               {status.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
//               {status.msg}
//             </div>
//           )}

//           <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
//             <div className="lg:col-span-5 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
//               <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-700 uppercase">Add Admin Text</h2>
//               <textarea 
//                 className="w-full border p-4 rounded-xl h-32 mb-4 focus:ring-2 focus:ring-yellow-400 outline-none bg-gray-50 text-gray-800"
//                 value={bannerText}
//                 onChange={(e) => setBannerText(e.target.value)}
//               ></textarea>
//               <button onClick={addBanner} className="w-full bg-yellow-500 text-black py-4 rounded-xl font-black hover:bg-yellow-600 transition shadow-md uppercase">Add to List</button>
//             </div>

//             <div className="lg:col-span-7 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
//               <h2 className="text-xl font-semibold mb-4 text-gray-700 uppercase">Manage Messages</h2>
//               <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
//                 {banners.map((b) => (
//                   <div key={b.id} className={`p-4 rounded-xl border flex justify-between items-center ${b.isActive ? 'border-yellow-400 bg-yellow-50/50' : 'opacity-60'}`}>
//                     <p className="text-gray-700 font-medium">{b.text}</p>
//                     <div className="flex gap-2">
//                       <button onClick={() => toggleBanner(b.id)} className={`p-2 rounded-lg ${b.isActive ? 'bg-green-500 text-white' : 'bg-gray-200'}`}><Power size={18} /></button>
//                       <button onClick={() => deleteBanner(b.id)} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-500 hover:text-white transition-all"><Trash2 size={18} /></button>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default BannerPage;